import { type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";

import { useDialog } from "../lib/hooks/useDialog";

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  eyebrow?: string;
  children: ReactNode;
  footer?: ReactNode;
  widthClass?: string;
  /** Element to focus on open; default is the first focusable element. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  closeOnEsc?: boolean;
}

/**
 * Centered dialog, portalled to <body>. Escape, backdrop and focus behaviour
 * live in `useDialog` (shared with `admin/Drawer`).
 */
export function Modal({
  open,
  onClose,
  title,
  eyebrow,
  children,
  footer,
  widthClass = "",
  initialFocusRef,
  closeOnEsc = true,
}: Props) {
  const { panelRef, titleId, backdropProps } = useDialog({
    open,
    onClose,
    kind: "modal",
    closeOnEsc,
    initialFocusRef,
  });
  if (!open) return null;
  return createPortal(
    <div className="modal-backdrop" {...backdropProps}>
      <div
        ref={panelRef}
        className={`modal ${widthClass}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal-header flex items-start justify-between gap-4">
          <div>
            {eyebrow ? <div className="eyebrow">{eyebrow}</div> : null}
            <h2 id={titleId}>{title}</h2>
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-icon"
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-footer">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}
